<template>
  <article class="manager-report-detail resident-report-detail">
    <header class="manager-report-detail__header resident-report-detail__header">
      <div class="manager-report-detail__header-section resident-report-detail__title-container">
        <button
          type="button"
          class="manager-report-detail__back"
          aria-label="Вернуться к списку отчётов"
          @click="emit('back')"
        >
          <img :src="LEFT_ARROW_ICON" alt="" aria-hidden="true" />
        </button>
        <h2 class="manager-report-detail__title resident-report-detail__title-container--title">
          Отчет
        </h2>
      </div>

      <div
        v-if="detail"
        class="manager-report-detail__header-actions resident-report-detail__header-actions"
      >
        <a-dropdown :trigger="['click']">
          <button type="button" class="manager-report-detail__status-select">
            <span
              class="manager-report-detail__status-dot"
              :class="`manager-report-detail__status-dot--${detail.statusModifier}`"
              aria-hidden="true"
            />
            <span class="manager-report-detail__status-label">{{ detail.status }}</span>
            <ArrowDownIcon class="manager-report-detail__status-icon" />
          </button>

          <template #overlay>
            <a-menu @click="handleStatusChange">
              <a-menu-item
                v-for="option in statusDropdownOptions"
                :key="option.code"
                :disabled="isStatusOptionDisabled(option)"
              >
                <div
                  class="manager-report-detail__status-option"
                  :class="{
                    'manager-report-detail__status-option--selected': option.name === detail.status,
                  }"
                >
                  <span
                    class="manager-report-detail__status-dot"
                    :class="`manager-report-detail__status-dot--${getStatusModifier(option.name)}`"
                    aria-hidden="true"
                  />
                  <span>{{ option.name }}</span>
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>

        <hr class="resident-report-detail__header-actions--divider" />

        <a-tooltip title="Скачать" placement="bottom">
          <a
            class="resident-report-detail__icon-action"
            :href="detail.downloadUrl"
            target="_blank"
            rel="noopener"
            aria-label="Скачать отчёт"
          >
            <img :src="DOWNLOAD_ICON" alt="" aria-hidden="true" />
          </a>
        </a-tooltip>
      </div>
    </header>

    <p v-if="loading" class="manager-report-detail__loading resident-report-detail__loading">
      Загрузка...
    </p>
    <p v-else-if="error" class="manager-report-detail__loading resident-report-detail__loading">
      {{ error }}
    </p>

    <template v-else-if="detail">
      <hr class="resident-report-detail__divider header-divider" />

      <section class="resident-report-detail__general-section">
        <span class="resident-report-detail__section-title">Общее</span>

        <div class="resident-report-detail__general-section--field-row">
          <div class="field-with-label">
            <span class="field-with-label__label">Название</span>
            <span class="field-with-label__value">{{ detail.title }}</span>
          </div>
          <div class="field-with-label">
            <span class="field-with-label__label">Дата создания</span>
            <span class="field-with-label__value">{{ detail.createdAt }}</span>
          </div>
        </div>

        <hr class="resident-report-detail__general-section--divider" />

        <div class="resident-report-detail__general-section--field-row">
          <div class="field-with-label">
            <span class="field-with-label__label">Организация</span>
            <span class="field-with-label__value">{{ detail.companyName }}</span>
          </div>
          <div class="field-with-label">
            <span class="field-with-label__label">Отчетный период</span>
            <span class="field-with-label__value">{{ detail.reportYear }}</span>
          </div>
        </div>
      </section>

      <hr class="resident-report-detail__divider" />

      <section class="resident-report-detail__metrics-section">
        <div class="resident-report-detail__metrics-section--title-container">
          <span class="resident-report-detail__metrics-section--title">Показатели</span>
          <span class="resident-report-detail__metrics-section--description">
            За отчетный выбранный период
          </span>
        </div>

        <table class="resident-report-detail__metrics-section--table">
          <thead>
            <tr>
              <th>Показатель</th>
              <th>План</th>
              <th>Факт</th>
              <th>Отклонение, %</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="metric in detail.metrics" :key="metric.label">
              <td>{{ metric.label }}</td>
              <td>{{ metric.plan }}</td>
              <td>{{ metric.fact }}</td>
              <td>{{ metric.deviation }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <hr class="resident-report-detail__divider" />

      <section class="resident-report-detail__details-section">
        <span class="resident-report-detail__section-title">Детали</span>

        <dl class="resident-report-detail__details-list">
          <div
            v-for="item in detail.details"
            :key="item.label"
            class="resident-report-detail__details-item"
          >
            <span class="resident-report-detail__details-item--label">{{ item.label }}</span>
            <span class="resident-report-detail__details-item--value">{{ item.value }}</span>
          </div>
        </dl>
      </section>

      <hr class="resident-report-detail__divider" />

      <section class="resident-report-detail__section">
        <span class="resident-report-detail__section-title">Документы</span>

        <ul v-if="detail.documents.length" class="resident-report-detail__documents">
          <li
            v-for="file in detail.documents"
            :key="file.url + file.name"
            class="resident-report-detail__document manager-report-detail__document"
          >
            <img :src="DOC_PDF_ICON" alt="" class="resident-report-detail__document-icon" />
            <div class="resident-report-detail__document-content">
              <a :href="file.url" target="_blank" rel="noopener" download>{{ file.name }}</a>
              <span>{{ file.format }} / {{ file.size }}</span>
            </div>
            <a
              :href="file.url"
              class="manager-report-detail__document-download"
              target="_blank"
              rel="noopener"
              download
              aria-label="Скачать документ"
            >
              <img :src="DOWNLOAD_ICON" />
            </a>
          </li>
        </ul>
        <p v-else class="resident-report-detail__empty-documents">Документы не загружены</p>
      </section>

      <template v-if="detail.signature">
        <hr class="resident-report-detail__divider" />

        <section class="manager-report-detail__signature-section">
          <span class="resident-report-detail__section-title">Эл. подпись</span>

          <div class="manager-report-detail__signature-status-row">
            <div class="field-with-label">
              <span class="field-with-label__label">Статус</span>
              <span class="manager-report-detail__signature-status">
                <img v-if="detail.signature.isValid" :src="VALID_ICON" />
                {{ detail.signature.isValid ? 'Действительно' : 'Недействительно' }}
              </span>
            </div>

            <div class="manager-report-detail__signature-actions">
              <a
                :href="detail.downloadUrl"
                class="btn btn-secondary btn-medium"
                target="_blank"
                rel="noopener"
              >
                Скачать подпись
              </a>
              <button
                type="button"
                class="btn btn-secondary btn-medium"
                :disabled="signChecking"
                @click="handleCheckSign"
              >
                {{ signChecking ? 'Проверка...' : 'Проверить подпись' }}
              </button>
            </div>
          </div>

          <a-divider class="manager-report-detail__divider" />

          <div class="manager-report-detail__signature-fields">
            <div class="field-with-label">
              <span class="field-with-label__label">Подписант</span>
              <span class="field-with-label__value">{{ detail.signature.signatory }}</span>
            </div>
            <div class="field-with-label">
              <span class="field-with-label__label">Организация</span>
              <span class="field-with-label__value">{{ detail.signature.organization }}</span>
            </div>
            <div class="field-with-label">
              <span class="field-with-label__label">УЦ</span>
              <span class="field-with-label__value">{{ detail.signature.certAuthority }}</span>
            </div>
            <div class="field-with-label">
              <span class="field-with-label__label">Дата / время подписания</span>
              <span class="field-with-label__value">{{ detail.signature.signedAt }}</span>
            </div>
          </div>
        </section>
      </template>
    </template>

    <ReportRevisionDrawer
      v-model:open="revisionDrawerOpen"
      :saving="revisionSaving"
      @submit="handleRevisionSubmit"
    />
  </article>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import type { MenuProps } from 'ant-design-vue';
import ArrowDownIcon from '@/js/components/icons/ArrowDownIcon.vue';
import {
  showStcErrorNotification,
  showStcInfoNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import { getAssetsPath } from '@/js/utils/getAssetsDir';
import ReportRevisionDrawer from './ReportRevisionDrawer.vue';
import {
  applyManagerReport,
  checkManagerReportSign,
  fetchManagerReportDetail,
  getManagerReportArchiveUrl,
  rejectManagerReport,
} from './manager-reports-api';
import {
  getStatusModifier,
  mapManagerReportToDetailView,
  type ManagerReportDetailViewModel,
} from './map-manager-report';
import type { ManagerReportSignCheckResult } from './types';
import type { ManagerReportRow, ManagerReportStatusOption } from './types';
import { MANAGER_REPORT_STATUS_OPTIONS } from './types';

const props = defineProps<{
  report: ManagerReportRow;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'updated'): void;
}>();

const LEFT_ARROW_ICON = getAssetsPath('/assets/images/icons/share-icons/left-arrow.svg');
const DOWNLOAD_ICON = getAssetsPath('/assets/images/icons/share-icons/download-icon.svg');
const DOC_PDF_ICON = getAssetsPath('/assets/images/icons/doc/doc_pdf.svg');
const VALID_ICON = getAssetsPath('/assets/images/icons/share-icons/success-circle-icon.svg');

const statusOptions = MANAGER_REPORT_STATUS_OPTIONS;
const statusDropdownOptions = computed(() =>
  statusOptions.filter((option) => option.code !== 'draft'),
);
const loading = ref(false);
const error = ref('');
const detail = ref<ManagerReportDetailViewModel | null>(null);
const statusSaving = ref(false);
const revisionDrawerOpen = ref(false);
const revisionSaving = ref(false);
const signChecking = ref(false);

function isReviewStatus(status?: string | null): boolean {
  return getStatusModifier(status) === 'review';
}

function isStatusOptionDisabled(option: ManagerReportStatusOption): boolean {
  if (!detail.value || statusSaving.value || revisionSaving.value) {
    return true;
  }

  if (option.name === detail.value.status) {
    return true;
  }

  const canChangeFromReview = isReviewStatus(detail.value.status);

  if (option.code === 'new') {
    return true;
  }

  return !canChangeFromReview;
}

async function loadSignatureCheck(reportId: number): Promise<void> {
  signChecking.value = true;

  try {
    const result = await checkManagerReportSign(reportId);

    if (detail.value?.signature) {
      detail.value.signature.isValid = Boolean(result.verify);
    }

    showSignatureCheckNotification(result);
  } catch (checkError) {
    showStcErrorNotification(
      checkError instanceof Error ? checkError.message : 'Не удалось проверить подпись',
    );
  } finally {
    signChecking.value = false;
  }
}

function formatSignCheckNotificationDescription(result: ManagerReportSignCheckResult): string {
  return [
    `Подписант: ${result.fio || '—'}`,
    `Организация: ${result.nameOrganization || '—'}`,
    `УЦ: ${result.certAuthority || '—'}`,
    `Сертификат, №: ${result.certNumber || '—'}`,
    `Срок действия: ${result.dateEndSert || '—'}`,
    `Дата подписания: ${result.dateSign || '—'}`,
  ].join('\n');
}

function showSignatureCheckNotification(result: ManagerReportSignCheckResult): void {
  const title = result.verify ? 'Подпись действительна' : 'Подпись недействительна';
  const description = formatSignCheckNotificationDescription(result);

  if (result.verify) {
    showStcSuccessNotification(description, title, { duration: 15 });
    return;
  }

  showStcInfoNotification(description, title, { duration: 15 });
}

async function handleCheckSign() {
  if (!detail.value?.signature || signChecking.value) {
    return;
  }

  await loadSignatureCheck(detail.value.id);
}

async function loadDetail() {
  loading.value = true;
  error.value = '';

  try {
    const report = await fetchManagerReportDetail(props.report.id, props.report);
    detail.value = mapManagerReportToDetailView(report, getManagerReportArchiveUrl(report.id));
  } catch (loadError) {
    detail.value = null;
    error.value = loadError instanceof Error ? loadError.message : 'Не удалось загрузить отчёт';
  } finally {
    loading.value = false;
  }
}

const handleStatusChange: MenuProps['onClick'] = async ({ key }) => {
  if (!detail.value || statusSaving.value || revisionSaving.value) {
    return;
  }

  const option = statusDropdownOptions.value.find((item) => item.code === String(key));
  if (!option || isStatusOptionDisabled(option)) {
    return;
  }

  if (option.code === 'revision') {
    revisionDrawerOpen.value = true;
    return;
  }

  statusSaving.value = true;

  try {
    if (option.code === 'accepted') {
      const successMessage = await applyManagerReport(detail.value.id);
      detail.value.status = option.name;
      detail.value.statusModifier = getStatusModifier(option.name);
      emit('updated');
      showStcSuccessNotification(successMessage || 'Отчёт принят');
      return;
    }

    showStcInfoNotification('Изменение на выбранный статус недоступно');
  } catch (updateError) {
    showStcErrorNotification(
      updateError instanceof Error ? updateError.message : 'Не удалось обновить статус отчёта',
    );
  } finally {
    statusSaving.value = false;
  }
};

async function handleRevisionSubmit(payload: { comment: string; files: File[] }) {
  if (!detail.value || revisionSaving.value) {
    return;
  }

  revisionSaving.value = true;

  try {
    const successMessage = await rejectManagerReport(detail.value.id, payload);

    const revisionOption = statusOptions.find((item) => item.code === 'revision');

    if (revisionOption) {
      detail.value.status = revisionOption.name;
      detail.value.statusModifier = getStatusModifier(revisionOption.name);
    }

    revisionDrawerOpen.value = false;
    emit('updated');
    showStcSuccessNotification(successMessage || 'Отчёт отправлен на доработку');
  } catch (updateError) {
    showStcErrorNotification(
      updateError instanceof Error ? updateError.message : 'Не удалось вернуть отчёт на доработку',
    );
  } finally {
    revisionSaving.value = false;
  }
}

watch(
  () => props.report.id,
  () => {
    void loadDetail();
  },
);

onMounted(() => {
  void loadDetail();
});
</script>
